/**
 * AGC: Potion Selector Component
 * AGC: Allows players to select one classic potion for battle
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect } from 'react'
import { useAccount } from 'wagmi'
import { Heart, Sparkles, Zap, Skull } from 'lucide-react'
import { buildBackendUrl } from '../utils/backendUrl'
import { CONTRACT_ADDRESSES, CLASSIC_POTION_1155_ABI } from '../hooks/useContract'
import { ethers } from 'ethers'

// AGC: Classic Potion Types (matching contract enum)
const POTION_TYPES = {
  // AGC: HEALING - Instant energy restore when used
  0: { name: 'Healing Potion', emoji: '❤️', icon: Heart, description: 'Instant +10 energy when used', image: '/potion_red.png' },
  // AGC: MANA - Persistent! Once used, boosts ALL buff activations for rest of battle
  1: { name: 'Mana Potion', emoji: '✨', icon: Sparkles, description: '+5% buff power for ALL remaining turns', image: '/potion_blue.png' },
  // AGC: DOUBLE STRIKE - Conditional! Only activates when you WIN that turn
  2: { name: 'Double Strike Potion', emoji: '⚡', icon: Zap, description: '+25% damage ONLY when you win', image: '/potion_purple.png' },
  // AGC: POISON - DoT (Damage over Time) 2.5 dmg × 5 turns = 12.5 total
  3: { name: 'Poison Potion', emoji: '☠️', icon: Skull, description: '2.5 dmg/turn × 5 turns (12.5 total)', image: '/potion_green.png' }
} as const

// AGC: Rarity border colors
const RARITY_BORDERS = {
  0: 'border-white',      // Common - White
  1: 'border-green-500', // Uncommon - Green
  2: 'border-blue-500',  // Rare - Blue
  3: 'border-purple-500', // Epic - Purple
  4: 'border-orange-500'  // Legendary - Orange
} as const

export interface PotionSelectorProps {
  playerAddress: string
  onPotionSelected: (potionTokenId: number | null) => void
  disabled?: boolean
  onNavigateToMarketplace?: () => void
  simulationMode?: boolean  // AGC: If true, show 1 of each potion type (simulation mode)
}

interface PotionData {
  tokenId: number
  potionType: number
  rarity: number
  magnitude: number
  balance: number
}

/**
 * AGC: Potion Selector - Select one classic potion for battle
 */
export const PotionSelector: React.FC<PotionSelectorProps> = ({
  playerAddress,
  onPotionSelected,
  disabled = false,
  onNavigateToMarketplace,
  simulationMode = false  // AGC: Simulation mode - show fake balances
}) => {
  const { address } = useAccount()
  const [potions, setPotions] = useState<PotionData[]>([])
  const [selectedPotion, setSelectedPotion] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  // AGC: Load player's classic potions
  useEffect(() => {
    const loadPotions = async () => {
      if (!playerAddress) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // AGC: Simulation mode - show 1 of each potion type (4 types: Healing, Mana, Double Strike, Poison)
        if (simulationMode) {
          console.log('🎮 Simulation mode: Showing 1 of each potion type')
          const simulationPotions: PotionData[] = [
            { tokenId: 1, potionType: 0, rarity: 0, magnitude: 10, balance: 1 }, // Healing
            { tokenId: 2, potionType: 1, rarity: 0, magnitude: 5, balance: 1 },  // Mana
            { tokenId: 3, potionType: 2, rarity: 0, magnitude: 25, balance: 1 }, // Double Strike
            { tokenId: 4, potionType: 3, rarity: 0, magnitude: 2.5, balance: 1 }  // Poison
          ]
          setPotions(simulationPotions)
          setLoading(false)
          return
        }

        if (!address) {
          setLoading(false)
          return
        }

        // AGC: Get RPC provider
        const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
        const provider = new ethers.JsonRpcProvider(rpcUrl)

        if (CONTRACT_ADDRESSES.CLASSIC_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          console.warn('⚠️ Classic Potion contract not configured')
          setPotions([])
          setLoading(false)
          return
        }

        const contract = new ethers.Contract(
          CONTRACT_ADDRESSES.CLASSIC_POTION_1155,
          CLASSIC_POTION_1155_ABI,
          provider
        )

        // AGC: Query player's potions from backend (or iterate through token IDs)
        // AGC: For now, we'll use a backend endpoint to get player's potions
        const backendUrl = buildBackendUrl()
        const response = await fetch(`${backendUrl}/api/classic-potions/player/${playerAddress}`)
        
        if (response.ok) {
          const potionList = await response.json()
          
          // AGC: Fetch metadata for each potion
          const potionDataPromises = potionList.map(async (potion: any) => {
            try {
              const metadata = await contract.getPotionMetadata(potion.tokenId)
              const balance = await contract.balanceOf(playerAddress, potion.tokenId)
              
              return {
                tokenId: Number(potion.tokenId),
                potionType: Number(metadata.potionType),
                rarity: Number(metadata.rarity),
                magnitude: Number(metadata.magnitude),
                balance: Number(balance)
              }
            } catch (error) {
              console.error(`Failed to load potion ${potion.tokenId}:`, error)
              return null
            }
          })

          const potionData = (await Promise.all(potionDataPromises)).filter(Boolean) as PotionData[]
          setPotions(potionData.filter(p => p.balance > 0))
        } else {
          console.warn('⚠️ Failed to load player potions')
          setPotions([])
        }
      } catch (error) {
        console.error('❌ Error loading potions:', error)
        setPotions([])
      } finally {
        setLoading(false)
      }
    }

    loadPotions()
  }, [playerAddress, address, simulationMode])

  // AGC: Handle potion selection (only one allowed)
  const handlePotionSelect = (tokenId: number) => {
    if (disabled) return

    if (selectedPotion === tokenId) {
      // AGC: Deselect if already selected
      setSelectedPotion(null)
      onPotionSelected(null)
    } else {
      // AGC: Select new potion (only one allowed)
      setSelectedPotion(tokenId)
      onPotionSelected(tokenId)
    }
  }

  // AGC: Clear selection
  const handleClear = () => {
    if (disabled) return
    setSelectedPotion(null)
    onPotionSelected(null)
  }

  if (loading) {
    return (
      <div className="p-4 text-center">
        <p className="text-slate-400 text-sm">Loading potions...</p>
      </div>
    )
  }

  // AGC: Show empty state in grid area (2x2 grid, always 4 slots)
  const emptySlots = potions.length === 0 ? 4 : (4 - (potions.length % 4)) % 4

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Select Classic Potion (1 max)</h3>
        {selectedPotion && (
          <button
            onClick={handleClear}
            disabled={disabled}
            className="text-xs text-slate-400 hover:text-slate-300 disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {/* AGC: Show empty state in grid if no potions */}
        {potions.length === 0 ? (
          <>
            <div className="col-span-2 border border-slate-600 rounded-lg p-6 text-center">
              <div className="text-4xl mb-2">🧪</div>
              <p className="text-slate-400 mb-2">No potions available</p>
              <p className="text-slate-500 text-sm mb-3">Visit the marketplace to buy potions</p>
              {onNavigateToMarketplace && (
                <button
                  onClick={onNavigateToMarketplace}
                  className="text-blue-400 hover:text-blue-300 text-sm underline"
                >
                  Buy potions in marketplace
                </button>
              )}
            </div>
            {/* AGC: Empty placeholder slots to maintain 2x2 grid */}
            <div className="border border-slate-700/30 rounded-lg p-3 opacity-30">
              <div className="flex items-center justify-center h-20">
                <span className="text-slate-600 text-3xl">⚗️</span>
              </div>
            </div>
            <div className="border border-slate-700/30 rounded-lg p-3 opacity-30">
              <div className="flex items-center justify-center h-20">
                <span className="text-slate-600 text-3xl">⚗️</span>
              </div>
            </div>
          </>
        ) : (
          (() => {
            // AGC: Always show 4 slots (2x2) - fill with empty placeholders if needed
            const slotsToShow = Math.ceil(potions.length / 4) * 4
            const emptySlots = slotsToShow - potions.length
            const allSlots = [...potions, ...Array(emptySlots).fill(null)]
            
            return allSlots.map((potion, index) => {
              if (!potion) {
                // AGC: Empty placeholder slot
                return (
                  <div
                    key={`empty-${index}`}
                    className="border border-slate-700/30 rounded-lg p-3 opacity-30"
                  >
                    <div className="flex items-center justify-center h-20">
                      <span className="text-slate-600 text-3xl">⚗️</span>
                    </div>
                  </div>
                )
              }
              
              const potionInfo = POTION_TYPES[potion.potionType as keyof typeof POTION_TYPES]
              const rarityBorder = RARITY_BORDERS[potion.rarity as keyof typeof RARITY_BORDERS] || 'border-white'
              const isSelected = selectedPotion === potion.tokenId
              const rarityName = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'][potion.rarity] || 'Unknown'

              return (
                <button
                  key={potion.tokenId}
                  onClick={() => handlePotionSelect(potion.tokenId)}
                  disabled={disabled || (!isSelected && selectedPotion !== null)}
                  className={`p-3 rounded-lg border-4 transition-all text-center relative overflow-hidden ${
                    isSelected
                      ? `${rarityBorder} bg-purple-500/20 text-white shadow-lg ring-2 ring-purple-400`
                      : selectedPotion !== null
                        ? 'border-slate-600 bg-slate-700/30 text-slate-500 cursor-not-allowed opacity-50'
                        : `${rarityBorder} bg-slate-700/30 text-gray-300 hover:bg-purple-400/10 hover:shadow-md hover:ring-1 hover:ring-purple-400/50`
                  }`}
                >
                  {/* AGC: Potion Image */}
                  <div className="flex items-center justify-center mb-2">
                    <div className="relative w-16 h-16">
                      <img
                        src={potionInfo?.image || '/potion_red.png'}
                        alt={potionInfo?.name || 'Potion'}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <span className="text-xs font-semibold block mb-1">{potionInfo?.name || 'Unknown'}</span>
                    <p className="text-[10px] text-slate-400 mb-1">{potionInfo?.description}</p>
                    <div className={`text-[9px] font-bold ${rarityBorder.replace('border-', 'text-')}`}>
                      {rarityName}
                    </div>
                    {isSelected && (
                      <div className="text-green-400 text-[10px] font-bold mt-1">✅ Selected</div>
                    )}
                  </div>
                </button>
              )
            })
          })()
        )}
      </div>
    </div>
  )
}

